import {
  DEFAULT_LAUNCHER_CLOSE_ATTEMPTS,
  SESSION_STORAGE_KEY_IS_LAUNCHER_OPEN,
  SESSION_STORAGE_KEY_LAUNCHER_CLOSE_ATTEMPTS,
  SESSION_STORAGE_KEY_NEXT_LAUNCHER_OPEN_AT,
  SESSION_STORAGE_KEY_SKIP_LAUNCHER_AUTO_OPEN,
} from '@lib/core/service/consts';

type TSessionValue<T> = {
  clear: () => void;
  get: () => T;
  set: (value: T) => void;
  subscribe: (listener: () => void) => () => void;
};

/**
 * Reactive sessionStorage-backed value.
 *
 * sessionStorage is the single source of truth so the value survives iframe reloads
 * across host pages. Every write notifies subscribers, which keeps all React consumers
 * (via useSyncExternalStore) and non-React writers (comms postMessage handlers) in sync.
 *
 * The in-memory cache guarantees stable snapshots for useSyncExternalStore and avoids
 * re-parsing storage on every render.
 */
const createSessionValue = <T>(
  key: string,
  parse: (stored: string | null) => T,
  serialize: (value: T) => string,
): TSessionValue<T> => {
  const listeners = new Set<() => void>();
  let cached = parse(sessionStorage.getItem(key));

  const notify = () => listeners.forEach(listener => listener());

  return {
    clear: () => {
      sessionStorage.removeItem(key);
      cached = parse(null);
      notify();
    },
    get: () => cached,
    set: (value: T) => {
      sessionStorage.setItem(key, serialize(value));
      cached = value;
      notify();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const IsLauncherOpen = createSessionValue<boolean>(
  SESSION_STORAGE_KEY_IS_LAUNCHER_OPEN,
  stored => (stored === null ? true : stored === 'true'),
  String,
);

export const LauncherCloseAttempts = createSessionValue<number>(
  SESSION_STORAGE_KEY_LAUNCHER_CLOSE_ATTEMPTS,
  stored => {
    const parsed = stored !== null ? Number(stored) : NaN;

    return Number.isFinite(parsed) ? parsed : DEFAULT_LAUNCHER_CLOSE_ATTEMPTS;
  },
  String,
);

export const NextLauncherOpenAt = createSessionValue<number | null>(
  SESSION_STORAGE_KEY_NEXT_LAUNCHER_OPEN_AT,
  stored => {
    if (stored === null) {
      return null;
    }

    const value = Number(stored);

    return Number.isFinite(value) ? value : null;
  },
  String,
);

// One-shot flag consumed by the launcher controller to skip auto-open on the next init pass.
// Set by comms when the host sends VH_WIDGET_CLOSED or LAUNCHER_INITIAL_CLOSED.
export const SkipLauncherAutoOpen = {
  checkAndClear(): boolean {
    const value = sessionStorage.getItem(SESSION_STORAGE_KEY_SKIP_LAUNCHER_AUTO_OPEN) === 'true';

    if (value) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY_SKIP_LAUNCHER_AUTO_OPEN);
    }

    return value;
  },
  set(): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY_SKIP_LAUNCHER_AUTO_OPEN, 'true');
  },
};
